import { Box, Button, Card, CardBody, CardFooter, CardHeader, Grid, Heading, Image, Layer, List, Nav, ResponsiveContext, Text } from "grommet"
import { Clear, Close, FormCheckmark } from "grommet-icons"
import { useGetMediasByCategoryQuery } from "../../../../api/mediasApi"
import { ReactSVG } from "react-svg"
import { useCallback, useContext, useEffect, useState } from "react"
import { ResponsiveGrid } from "../../../ResponsiveGrid"

// if size if xsmall, we only 1 column
// if size if small, we only 1 column
// if size if medium, we only 2 column
// if size if large or xlarge, we 2 columns with 2/3 1/3 proportions
const columns = {
    xsmall: ["flex"],
    small: ["flex", "flex"],
    medium: ["small", "small", "small"],
    large: ["small", "small", "small"],
    xlarge: ["small", "small", "small"]
};

// if size if xsmall, we have 2 rows
// if size if small, we have 2 rows
// if size if medium, we have 2 rows
// if size if large or xlarge, we have 1 row
const rows = {
    xsmall: ["flex"],
    small: ["flex"],
    medium: ["flex"],
    large: ["flex"],
    xlarge: ["flex"]
};

const gap = {
    xsmall: 'small',
    small: 'small',
    medium: 'small',
    large: 'small',
    xlarge: 'small'
}

const layoutAreas = {
    xsmall: {
        direction: 'column',
        categoryProps: { flex: "grow" },
        gridProps: { }
    },
    small: {
        direction: 'column',
        categoryProps: { flex: "grow" },
        gridProps: { }
    },
    medium: {
        direction: 'row',
        categoryProps: { },
        gridProps: { flex: "grow" }
    },
    large: {
        direction: 'row',
        categoryProps: { },
        gridProps: { flex: "grow" }
    },
    xlarge: {
        direction: 'row',
        categoryProps: { },
        gridProps: { flex: "grow" }
    },
}

function groupByCategoryCode(items) {
	return items.reduce((acc, item) => {
		const code = item.category.code;
		if (!acc[code]) {
			acc[code] = {
				category: item.category,
				items: [],
			};
		}
		acc[code].items.push(item);
		return acc;
	}, {});
}

function getUniqueCategories(items) {
	const seen = new Map();
	items.forEach((item) => {
		const code = item.category.code;
		if (!seen.has(code)) {
			seen.set(code, item.category);
		}
	});
	return Array.from(seen.values());
}

const MediaSelectModal = ({ category, selected, onClose, onClick }) => {

    const size = useContext(ResponsiveContext);

    const { data: medias, isSuccess: isMediaLoaded } = useGetMediasByCategoryQuery({ categoryCode: category, searchByParent: true });
    const [categories, setCategories] = useState([]);
    const [categoryMedias, setCategoryMedias] = useState();
    const [activeCategory, setActiveCategory] = useState();

    useEffect(() => {
        if (isMediaLoaded) {
            const uniqueCategoryies = getUniqueCategories(medias);
            setCategories(uniqueCategoryies);

            const categoryMediasByCode = groupByCategoryCode(medias);
            setCategoryMedias(categoryMediasByCode)
        }
    }, [medias, isMediaLoaded])

    useEffect(() => {
        if (!categoryMedias) return;

        if (selected && selected.category) {
            setActiveCategory(categoryMedias[selected.category.code]);
        } else {
            const firstKey = Object.keys(categoryMedias)[0];

            setActiveCategory(categoryMedias[firstKey]);
        }
    }, [categoryMedias, selected])

    const handleCategorySelect = useCallback((category) => {
        if (category.type === 'system') {
            const firstKey = Object.keys(categoryMedias)[0];

            setActiveCategory(categoryMedias[firstKey]);
        } else {
            setActiveCategory(categoryMedias[category.code]);
        }
    }, [categoryMedias])

    return (
        <Layer onClickOutside={onClose}>
            <Card>
                <CardHeader>
                    <Heading level={2} weight={'bold'}>Select a file</Heading>
                    <Button icon={<Close />} onClick={onClose} />
                </CardHeader>
                <CardBody direction={layoutAreas[size].direction} pad={{ left: 'medium', right: 'medium' }}>
                    <Box {...layoutAreas[size].categoryProps}>
                        <Nav gap="small" pad={{ right: 'medium' }}>
                            {categories.map(item => (<SidebarButton item={item} active={item.code === activeCategory?.category.code} onClick={handleCategorySelect} />))}
                        </Nav>
                    </Box>
                    <Box {...layoutAreas[size].gridProps} height={'60vh'} overflow="auto">
                        {activeCategory && <ResponsiveGrid
                            overrideRows={rows}
                            overrideColumns={columns}
                            // areas={areas}
                            gap={gap[size]}
                            margin="none"
                        >
                            {[{ name: 'None', icon: <Clear style={{ height: '45px', width: '45px' }}/>, type: 'system', url: '' }, ...activeCategory.items].map((media) => <MediaCard media={media} onClick={() => onClick(media)} selected={media.id === selected?.id} />)}
                        </ResponsiveGrid>}
                    </Box>
                </CardBody>
                <CardFooter justify="end" pad={'medium'}>
                    <Button label="Select a file" icon={<FormCheckmark />} primary />
                </CardFooter>
            </Card>
        </Layer>
    )
}

const SidebarButton = ({ item, active, onClick }) => {

    const [isActiveHover, setActiveHover] = useState(false);

    return (
        <Box direction="row" pad={{ vertical: 'xsmall', horizontal: '14px' }} onClick={() => onClick(item)} style={{ position: "relative" }} round="xsmall" background={active ? 'background-contrast' : 'none'} hoverIndicator focusIndicator={false} onMouseEnter={() => setActiveHover(true)} onMouseLeave={() => setActiveHover(false)}>
            {(isActiveHover || active) && <Box width={'6px'} style={{ position: "absolute", left: 0, top: 0, bottom: 0, borderTopLeftRadius: '6px', borderBottomLeftRadius: '6px' }} background={'brand'}></Box>}
            <Button label={<Text size="small" style={{ whiteSpace: 'nowrap' }}>{item.name}</Text>} plain fill justify="start" />
        </Box>
    )
}

const MediaCard = ({ media, onClick, selected = false }) => {

    return (
        <Box key={media.id} align="center" justify="center" border={selected ? { color: 'brand', size: '2px' } : null} background={'background-contrast'} round="small" pad={'small'} gap="small" onClick={onClick}>
            <Box height={'170px'} width={'170px'} align="center" justify="center">
                {media.type === 'system' && media.icon}
                {media.type === 'vector' && <ReactSVG className="pin-vector" src={media.url} beforeInjection={(svg) => {
                    svg.setAttribute('style', 'width: 45px;height:45px')
                }}/>}
                {media.type === 'image' && <Box><Image fit="contain" src={media.url} /></Box>}
                {media.type === 'video' && <Box><video src={media.url} autoPlay loop /></Box>}
            </Box>
            <Box align="center" justify="center">
                <Text textAlign="center">{media.name}</Text>
            </Box>
        </Box>
    )
}

export default MediaSelectModal;